"use client"

/* eslint-disable react/no-unescaped-entities */
import { LinkButton } from "@/components/ui/link-button"
import { Maybe, ProductInterface } from "@/types/graphql.prod"
import { useEffect, useRef } from "react"
import { CartItem, ExtendedCartItemInterface } from "./cart-item"
import { ProductListSkeleton } from "./productlist-skeleton"
import { useMiniCartStore } from "../use-new-minicart-store"
import ProductsSlider from "./products-slider"
import ProductsSliderSkeleton from "./products-slider-skeleton"

// 创建一个节流函数
function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0

  return (...args: Parameters<T>) => {
    const now = Date.now()

    if (now - lastCall >= delay) {
      func(...args)
      lastCall = now
    }
  }
}

export function MiniCartContent({
  miniCartLoading,
  productList,
  crossData,
  crossDataLoading,
  refetch,
  headerRef,
}: {
  miniCartLoading: boolean
  productList: Maybe<ExtendedCartItemInterface>[]
  crossData: Maybe<ProductInterface>[]
  crossDataLoading: boolean
  refetch?: any
  headerRef: any
}) {
  const scrollArea = useRef<HTMLDivElement>(null)
  const setOpen = useMiniCartStore((state) => state?.setDialogOpen)

  useEffect(() => {
    const header = headerRef.current
    const scrollArearef = scrollArea.current

    if (!header) {
      return
    }

    if (!scrollArearef) {
      return
    }

    const handleScroll = throttle(() => {
      const currentScrollY = scrollArearef?.scrollTop

      if (currentScrollY > 50) {
        // 向下滚动且超过100px时隐藏
        header.classList.add("shadow-md") // Add shadow when visible
      } else {
        // 向上滚动时显示
        header.classList.remove("shadow-md") // Remove shadow when hidden
      }
    }, 10) // 10ms的节流时间

    scrollArearef.addEventListener("scroll", handleScroll, {
      passive: true,
    })
    scrollArearef.addEventListener("resize", handleScroll, {
      passive: true,
    })

    return () => {
      scrollArearef.removeEventListener("scroll", handleScroll)
      scrollArearef.removeEventListener("resize", handleScroll)
    }
  }, [headerRef])

  return (
    <div
      className="flex-1  gap-4 overflow-x-hidden lg_min-h-[400px]"
      ref={scrollArea}
    >
      <div className="px-[12px] lg_px-[24px]">
        {miniCartLoading ? (
          <ProductListSkeleton />
        ) : Array.isArray(productList) && productList?.length > 0 ? (
          productList?.map((item) => <CartItem key={item?.uid} item={item} />)
        ) : (
          <div className="flex h-[200px] flex-col items-center justify-center gap-[25px]">
            <span className="font-sans font-[500] text-[#2f2f2f]">
              Your cart is empty
            </span>

            <LinkButton
              onClick={() => setOpen(false)}
              href="/bird-feeders"
              className="h-[40px] rounded-[10px] font-poppins font-[600] text-white lg_h-[54px]"
            >
              Start Shopping
            </LinkButton>
          </div>
        )}
      </div>
      {crossDataLoading ? (
        <div className="px-[12px] pb-4 pt-[20px] lg_px-[24px]">
          <div className="mb-[15px] h-[28px] w-full rounded bg-[#F7F7F5]" />

          <ProductsSliderSkeleton />
        </div>
      ) : (
        <>
          {Array.isArray(crossData) && crossData?.length > 0 && (
            <div className="px-[12px] pb-4 pt-[20px] lg_px-[24px]">
              <h2 className="mb-[15px] font-poppins text-[20px] font-[600] leading-[1.4] text-title">
                Need a little more?
              </h2>
              <ProductsSlider crossData={crossData} refetch={refetch} />
            </div>
          )}
        </>
      )}
    </div>
  )
}
